<template>
  <el-dialog
    id="verify-otp-dialog"
    ref="verifyOTPDialog"
    top="30vh"
    width="50rem"
    :title="`Profile Verification`"
    :visible.sync="showVerifyOTPDialog"
    :destroy-on-close="true"
    :append-to-body="true"
    :close-on-click-modal="false"
  >
    <el-form
      class="verification-form"
      ref="verificationForm"
      label-position="top"
      label-width="150px"
      :model="verificationForm"
      @submit.native.prevent
    >
      <div>
        <p>Please provide the mobile number of the person who requested profile enhancement.</p>
        <el-form-item label="Mobile Number" prop="mobile" :error="mobileError">
          <template #label>
            <span>
              Mobile Number
              <i class="el-icon-phone" style="color: #909399; margin-left: 5px"></i>
            </span>
          </template>
          <el-input
            clearable
            oninput="this.value = this.value.replace(/[^0-9]/g, '');"
            placeholder="Enter Mobile Number"
            style="max-width: 350px"
            :disabled="otpSent"
            v-model="verificationForm.mobile_number"
            @blur="validateMobileNumber"
            @input="handleMobileNumberInput"
          />
        </el-form-item>
      </div>

      <div v-if="otpSent" class="fixed-otp-height">
        <p>Please enter the 5-digit OTP sent to the above mobile number:</p>
        <el-form-item label="OTP" prop="otp" :error="otpError">
          <template #label>
            <span
              >OTP
              <i class="el-icon-key" style="color: #909399; margin-left: 5px"></i>
            </span>
          </template>
          <el-input
            clearable
            :type="showPassword ? 'text' : 'password'"
            maxlength="5"
            @input="clearOtpError"
            oninput="this.value = this.value.replace(/[^0-9]/g, '');"
            placeholder="Enter OTP"
            style="max-width: 250px"
            v-model="verificationForm.otp"
          >
            <template v-slot:append>
              <el-button v-if="showPassword" @click="togglePasswordVisibility"
                ><i class="fa fa-fw fa-eye-slash"></i
              ></el-button>
              <el-button v-else @click="togglePasswordVisibility"
                ><i class="fa fa-fw fa-eye"></i
              ></el-button>
            </template>
          </el-input>
        </el-form-item>
        <p id="countdown" class="text-muted" v-show="showCountdown" style="margin-top: 10px">
          Time remaining: {{ countdown }}
        </p>
        <p id="resend" class="text-muted" v-show="showResend" style="margin-top: 10px">
          Didn't receive OTP? <a @click="resendOTP">Resend</a>
        </p>
      </div>
    </el-form>
    <span class="footer">
      <div class="pull-right">
        <el-button
          type="danger"
          size="small"
          icon="el-icon-close"
          class="ibox-tool-btn"
          @click="$emit('close')"
          plain
          >Cancel</el-button
        >
        <el-button
          v-if="otpSent"
          type="success"
          size="small"
          icon="el-icon-success"
          class="ibox-tool-btn"
          @click="verifyOTP"
          :loading="loading"
          plain
          >Verify OTP</el-button
        >
        <el-button
          v-if="!otpSent"
          type="primary"
          size="small"
          icon="el-icon-message"
          class="ibox-tool-btn"
          @click="sendOTP"
          :loading="loading"
          plain
          >Send OTP</el-button
        >
      </div>
    </span>
  </el-dialog>
</template>


<script>
import UpdateEditModeMutation from '@/graphql/permission/editProfilePermissionModeMutation.gql';
import sendPermissionOTP from '@/graphql/permission/sendOTPMutation.gql';
import verifyPermissionOTP from '@/graphql/permission/verifyOTPMutation.gql';

export default {
  name: 'VerifyOTPManage',
  computed: {
    showVerifyOTPDialog: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('close', val);
      },
    },
  },
  data: () => ({
    loading: false,
    showPassword: false,
    otpSent: false,
    showCountdown: false,
    showResend: false,
    isValidPhoneNumber: true,
    countdown: '02:00',
    verificationForm: {
      mobile_number: '',
      otp: '',
    },
    mobileError: '',
    otpError: '',
    countdownInterval: null,
  }),
  created() {
    this.verificationForm.mobile_number = this.initialMobileNumber || '';
  },
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    sendOTP() {
      this.validateMobileNumber();
      if (this.mobileError) return;
      this.loading = true;

      const mobileNumber = this.verificationForm.mobile_number;

      const OTP = {
        profile_id: this.initialProfile.id,
        destination: mobileNumber,
        source: 'live',
      };

      this.$apollo
        .mutate({
          mutation: sendPermissionOTP,
          variables: { query: OTP },
          client: 'apiv2',
        })
        .then(() => {
          this.loading = false;
          this.otpSent = true;
          this.startCountdown();
          this.$emit('requestOTP', mobileNumber);
        })
        .catch(() => {
          this.loading = false;
          this.$notify({
            title: 'Error',
            message: 'Failed to send OTP. Please try again.',
            type: 'error',
          });
        });
    },
    async verifyOTP() {
      await this.validateOTP();
      if (this.otpError) return;

      this.loading = true;

      const profileToUpdate = {
        profile_id: this.initialProfile.id,
        profile_edit_status: true,
        field_key: 'profile_edit',
        category: 'meta',
      };
      try {
        await this.$apollo.mutate({
          mutation: UpdateEditModeMutation,
          variables: { query: profileToUpdate },
          client: 'apiv2',
        });
        this.$notify({
          title: 'Success',
          message: 'OTP verified successfully',
          type: 'success',
        });
        this.$emit('verifyOTP');
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to verify OTP. Please try Again',
          type: 'error',
        });
      } finally {
        this.loading = false;
      }
    },
    validateOTP() {
      return new Promise(resolve => {
        const OTP = (this.verificationForm.otp || '').trim();
        const mobileNumber = this.verificationForm.mobile_number;

        this.otpError = OTP.length === 0 ? 'OTP is required' : '';
        if (this.otpError) return;

        this.loading = true;

        const OTPData = {
          profile_id: this.initialProfile.id,
          originator: mobileNumber,
          otp: OTP,
        };

        this.$apollo
          .mutate({
            mutation: verifyPermissionOTP,
            variables: { query: OTPData },
            client: 'apiv2',
          })
          .then(results => {
            this.loading = false;
            const { status } = results.data.verifyPermissionProfileOtp;
            if (status === 'invalid') {
              this.otpError = 'OTP is Invalid. Please try again';
              resolve(false);
            } else if (status === 'expired') {
              this.otpError = 'OTP has expired. Please request a new one';
              resolve(false);
            }
            resolve(true);
          })
          .catch(() => {
            this.loading = false;
            this.$notify({
              title: 'Error',
              message: 'Failed to verify OTP. Please try again.',
              type: 'error',
            });
            resolve(false);
          });
      });
    },
    resendOTP() {
      this.validateMobileNumber();
      if (this.mobileError) return;
      this.showResend = false;

      const mobileNumber = this.verificationForm.mobile_number;

      const reSendOTP = {
        profile_id: this.initialProfile.id,
        destination: mobileNumber,
        source: 'live',
      };

      this.$apollo
        .mutate({
          mutation: sendPermissionOTP,
          variables: { query: reSendOTP },
          client: 'apiv2',
        })
        .then(() => {
          this.startCountdown();
          this.$emit('requestOTP', mobileNumber);
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            message: 'Failed to send OTP. Please try again.',
            type: 'error',
          });
        });
    },
    validateMobileNumber() {
      const mobileNumber = (this.verificationForm.mobile_number || '').trim();
      const phoneRegex = /^(\+\d{1,3}\s?)?\d{9,}$/;

      this.isValidPhoneNumber = phoneRegex.test(mobileNumber);

      if (mobileNumber.length === 0 || !this.isValidPhoneNumber) {
        this.mobileError = 'Mobile number is invalid';
      } else {
        this.mobileError = '';
      }
    },
    clearOtpError() {
      this.otpError = '';
    },
    handleMobileNumberInput() {
      this.mobileError = '';
      this.refresh();
    },
    startCountdown() {
      this.showCountdown = true;
      let totalSeconds = 120;
      this.countdownInterval = setInterval(() => {
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        this.countdown = `${minutes.toString().padStart(2, '0')}:${seconds
          .toString()
          .padStart(2, '0')}`;
        if (totalSeconds === 0) {
          clearInterval(this.countdownInterval);
          this.showCountdown = false;
          this.showResend = true;
        } else {
          totalSeconds -= 1;
        }
      }, 1000);
    },
    refresh() {
      this.otpSent = false;
      this.showCountdown = false;
      this.showResend = false;
      this.countdown = '02:00';
      clearInterval(this.countdownInterval);
    },
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initialProfile: {
      type: Object,
      required: true,
    },
    initialMobileNumber: {
      type: String,
      required: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.verification-form {
  margin-bottom: 40px;
}
.info-text {
  margin-bottom: 30px;
}
.fixed-otp-height {
  min-height: 18rem;
  overflow: auto;
}
</style>


// WEBPACK FOOTER //
// VerifyOTPDialog.vue?0723aeaa